import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Row, Col, Space, Typography, Tag, Statistic, Button, Modal, Input, InputNumber, message, Progress, Alert, Tabs, Switch } from 'antd';
import { RocketOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';
import api from '../services/api';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

const { Title, Text, Paragraph } = Typography;

const Reengagement = () => {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [template, setTemplate] = useState('futezap_reengaja_v2');
  const [instance, setInstance] = useState('instance-42-waba-futezap-organizer');
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('ouro');
  const [customOffer, setCustomOffer] = useState(false);
  const [customPct, setCustomPct] = useState(30);
  const [customMonths, setCustomMonths] = useState(3);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get('/monitoring/reengagement/candidates'),
        api.get('/monitoring/reengagement/stats'),
      ]);
      setData(c.data);
      setStats(s.data);
    } catch (err) {
      message.error('Erro: ' + (err.response?.data?.error || err.message));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (selectedIds.length === 0) return message.warning('Selecione ao menos 1 time');
    if (!template) return message.warning('Informe template_name cadastrado no WABA');
    setSending(true);
    try {
      const payload = {
        customer_ids: selectedIds,
        template_name: template,
        instance_name: instance,
      };
      if (customOffer) {
        payload.discount_percent = customPct;
        payload.discount_months = customMonths;
      }
      const r = await api.post('/monitoring/reengagement/send', payload);
      const ok = r.data.results.filter(x => x.ok).length;
      const fail = r.data.results.length - ok;
      message.success(`${ok} enviados, ${fail} falhas`);
      setSelectedIds([]);
      setModalOpen(false);
      load();
    } catch (err) {
      message.error('Erro: ' + (err.response?.data?.error || err.message));
    } finally { setSending(false); }
  };

  const columns = (segment) => [
    { title: 'Time', dataIndex: 'game_name', width: 200,
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <a href={`/customers/${r.customer_id}/ficha`} target="_blank" rel="noreferrer">{v || '(sem nome)'}</a>
          <Text type="secondary" style={{ fontSize: 11 }}>#{r.customer_id} • {r.cellphone}</Text>
        </Space>
      ),
    },
    { title: 'Status', dataIndex: 'status', width: 130,
      render: v => <Tag color={v === 'CANCELADO' ? 'red' : 'orange'}>{v}</Tag> },
    { title: 'Plano', dataIndex: 'plan_name', width: 130 },
    { title: 'Pagamentos', dataIndex: 'paid_count', width: 90, align: 'center',
      sorter: (a, b) => a.paid_count - b.paid_count },
    { title: 'LTV', dataIndex: 'ltv', width: 100, align: 'right',
      render: v => <Text strong style={{ color: '#52c41a' }}>R$ {Number(v).toFixed(2)}</Text>,
      sorter: (a, b) => a.ltv - b.ltv, defaultSortOrder: 'descend' },
    { title: 'Dias s/ pgto', dataIndex: 'days_since_churn', width: 130, align: 'center',
      sorter: (a, b) => (a.days_since_churn || 0) - (b.days_since_churn || 0),
      render: (v, r) => {
        if (v === null || v === undefined) return '—';
        const color = r.temperature === 'quente' ? 'green' : r.temperature === 'morno' ? 'orange' : 'default';
        const icon = r.temperature === 'quente' ? '🔥' : r.temperature === 'morno' ? '☀️' : '❄️';
        return <Tag color={color}>{icon} {v}d</Tag>;
      } },
    { title: 'Último envio', dataIndex: 'last_lead', width: 180, align: 'center',
      render: (l) => {
        if (!l) return <Tag color="default">Nunca enviado</Tag>;
        const st = l.status;
        const color = st === 'converted' ? 'green' : st === 'clicked' ? 'cyan' : st === 'read' ? 'blue' : st === 'delivered' ? 'geekblue' : st === 'sent' ? 'gold' : st === 'failed' ? 'red' : 'default';
        const label = { pending: '⏳ Pendente', sent: '📤 Enviado', delivered: '✓ Entregue', read: '👀 Lido', clicked: '🎯 Clicou', converted: '✅ Convertido', failed: '❌ Falhou', opted_out: '🚫 Opt-out' }[st] || st;
        return (
          <Space direction="vertical" size={0}>
            <Tag color={color}>{label}</Tag>
            <Text type="secondary" style={{ fontSize: 10 }}>{dayjs(l.created_at).fromNow()}</Text>
          </Space>
        );
      } },
    { title: 'Cooldown', dataIndex: 'cooldown_active', width: 100, align: 'center',
      render: v => v ? <Tag color="red">Cooldown 90d</Tag> : <Tag color="green">Livre</Tag> },
  ];

  const rowSelection = {
    selectedRowKeys: selectedIds,
    onChange: (keys) => setSelectedIds(keys),
    getCheckboxProps: (r) => ({ disabled: r.cooldown_active }),
  };

  const segments = data ? [
    { key: 'ouro', label: `🥇 Ouro (${data.by_segment.ouro.count})`, ...data.by_segment.ouro },
    { key: 'prata', label: `🥈 Prata (${data.by_segment.prata.count})`, ...data.by_segment.prata },
    { key: 'bronze', label: `🥉 Bronze (${data.by_segment.bronze.count})`, ...data.by_segment.bronze },
  ] : [];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}><RocketOutlined /> Reengajamento de churn</Title>
          <Text type="secondary">Times que já pagaram e saíram — abordagem por segmento (LTV × tempo de saída).</Text>
        </Col>
        <Col>
          <Button type="primary" icon={<SendOutlined />} disabled={selectedIds.length === 0} onClick={() => setModalOpen(true)}>
            Disparar template ({selectedIds.length})
          </Button>
        </Col>
      </Row>

      {data && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}><Card><Statistic title="Total candidatos" value={data.total} /></Card></Col>
          <Col span={6}><Card><Statistic title="🥇 Ouro (LTV>300, <60d)" value={data.by_segment.ouro.count} valueStyle={{ color: '#faad14' }} /></Card></Col>
          <Col span={6}><Card><Statistic title="🥈 Prata (LTV 100-300)" value={data.by_segment.prata.count} valueStyle={{ color: '#8c8c8c' }} /></Card></Col>
          <Col span={6}><Card><Statistic title="🥉 Bronze (LTV 30-100)" value={data.by_segment.bronze.count} valueStyle={{ color: '#d46b08' }} /></Card></Col>
        </Row>
      )}

      <Alert type="info" showIcon style={{ marginBottom: 16 }}
        message="Como funciona"
        description={
          <ul style={{ marginBottom: 0 }}>
            <li>Selecione times, escolha o template WABA correspondente ao segmento</li>
            <li>Ao clicar no link do template, o lead vai pra <code>/reativar/:code</code> — sistema notifica grupo suporte automaticamente</li>
            <li>Cooldown: mesmo time não recebe outra campanha por 90d</li>
            <li>Templates precisam estar cadastrados na Meta com variáveis: {'{{1}}'}=nome time, {'{{2}}'}=desconto%, {'{{3}}'}=link</li>
          </ul>
        } />

      <Tabs activeKey={activeTab} onChange={setActiveTab}
        items={segments.map(seg => ({
          key: seg.key,
          label: seg.label,
          children: (
            <Card size="small">
              <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                Oferta padrão: <Tag color="green">{seg.offer?.discount_percent}% off por {seg.offer?.discount_months} meses</Tag>
              </Text>
              <Table
                rowKey="customer_id"
                dataSource={seg.leads || []}
                columns={columns(seg.key)}
                rowSelection={activeTab === seg.key ? rowSelection : undefined}
                pagination={{ pageSize: 20 }}
                size="small"
                scroll={{ x: 'max-content' }}
              />
            </Card>
          ),
        }))} />

      {stats?.campaigns?.length > 0 && (
        <Card title="Campanhas recentes" size="small" style={{ marginTop: 16 }}>
          <Table
            rowKey="id"
            dataSource={stats.campaigns}
            pagination={{ pageSize: 10 }}
            size="small"
            columns={[
              { title: 'Data', dataIndex: 'created_at', width: 130,
                render: v => dayjs(v).format('DD/MM HH:mm') },
              { title: 'Nome', dataIndex: 'name' },
              { title: 'Template', dataIndex: 'template_name', width: 200 },
              { title: 'Enviados', dataIndex: 'sent', width: 90, align: 'center' },
              { title: 'Entregues', dataIndex: 'delivered', width: 90, align: 'center' },
              { title: 'Lidos', dataIndex: 'read', width: 90, align: 'center' },
              { title: 'Cliques', dataIndex: 'clicked', width: 90, align: 'center',
                render: v => <Text strong style={{ color: '#1890ff' }}>{v}</Text> },
              { title: 'Convertidos', dataIndex: 'converted', width: 110, align: 'center',
                render: v => <Text strong style={{ color: '#52c41a' }}>{v}</Text> },
              { title: 'Falhas', dataIndex: 'failed', width: 80, align: 'center' },
            ]}
          />
        </Card>
      )}

      <Modal open={modalOpen} onCancel={() => setModalOpen(false)} title="Confirmar envio"
        width={520}
        footer={[
          <Button key="c" onClick={() => setModalOpen(false)}>Cancelar</Button>,
          <Button key="s" type="primary" loading={sending} onClick={send}>Enviar {selectedIds.length}</Button>,
        ]}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert type="warning" showIcon message={`Vai disparar template WABA REAL pra ${selectedIds.length} organizadores.`} />
          <div>
            <Text strong>Template name (WABA):</Text>
            <Input value={template} onChange={e => setTemplate(e.target.value)} placeholder="futezap_reengaja_v2" />
          </div>
          <div>
            <Text strong>Instância:</Text>
            <Input value={instance} onChange={e => setInstance(e.target.value)} />
          </div>
          <Card size="small" style={{ background: customOffer ? '#fffbe6' : undefined }}>
            <Space style={{ marginBottom: customOffer ? 12 : 0 }}>
              <Switch checked={customOffer} onChange={setCustomOffer} />
              <Text strong>Customizar oferta</Text>
              {!customOffer && <Text type="secondary" style={{ fontSize: 12 }}>(padrão do segmento)</Text>}
            </Space>
            {customOffer && (
              <>
                <Row gutter={8}>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Desconto %</Text>
                    <InputNumber min={5} max={90} value={customPct} onChange={setCustomPct}
                      formatter={v => `${v}%`} parser={v => v.replace('%','')}
                      style={{ width: '100%' }} />
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Meses</Text>
                    <InputNumber min={1} max={12} value={customMonths} onChange={setCustomMonths}
                      style={{ width: '100%' }} />
                  </Col>
                </Row>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                  Valor PIX estimado (plano R$ 29,90): <Text strong>R$ {(29.9 * (1 - customPct/100) * customMonths).toFixed(2)}</Text>
                  {' — '}precisa estar na whitelist do n8n
                </Text>
              </>
            )}
          </Card>
        </Space>
      </Modal>
    </div>
  );
};

export default Reengagement;
